<template>
  <transition name="sidebar-slide">
    <div
      id="sidebar-panel"
      v-show="sidebarOpen"
      :class="{ open: sidebarOpen, expanded: expanded, mobile: mobile }"
      class="blue-grey lighten-5 elevation-1"
    >
      <ul
        id="sidebar-actions"
        class="text-xs-right white"
        v-if="!isBroadcast || broadcastTeacher || broadcastStudent"
        v-show="showSidebarActions"
      >
        <li v-if="showInteractionsTab && !broadcastStudent && !isLiveSession">
          <v-btn
            flat
            icon
            class="ma-0"
            v-if="!previousDisabled"
            aria-label="Select Previous Interaction"
            @click="requestSetActiveInteraction('previous')"
          >
            <v-icon>skip_previous</v-icon>
          </v-btn>
        </li>
        <li v-if="showInteractionsTab && !broadcastStudent && !isLiveSession">
          <v-btn
            flat
            icon
            class="ma-0"
            v-if="!nextDisabled"
            aria-label="Select Next Interaction"
            @click="requestSetActiveInteraction('next')"
          >
            <v-icon>skip_next</v-icon>
          </v-btn>
        </li>
        <li id="resize-side-panel" v-show="sidebarStyle !== 'portrait'">
          <v-btn
            v-show="showExpandButton"
            flat
            icon
            class="ma-0"
            @click="expand"
            :aria-label="(expanded ? 'Shrink' : 'Expand') + ' Sidebar'"
          >
            <v-icon v-show="sidebarStyle !== 'portrait'">{{ expanded ? 'remove' : 'add' }}</v-icon>
          </v-btn>
        </li>
        <li v-show="sidebarStyle !== 'portrait'">
          <v-btn flat icon class="ma-0" @click="collapse" aria-label="Collapse Sidebar">
            <v-icon>close</v-icon>
          </v-btn>
        </li>
      </ul>
      <div class="review-wrapper" v-show="!showInteractionsTab">
        <div id="sidebarTabs" class="sidebar-tabs">
          <div
            class="sidebar-tab"
            role="button"
            :aria-label="`Open ${termForBulb.capitalize()} Review Tab`"
            tabindex="0"
            @click="setSidebarTab('bulb-info-tab')"
            @keyup.enter="setSidebarTab('bulb-info-tab')"
            :class="{ active: sidebarTab === 'bulb-info-tab' }"
          >
            Review <v-icon v-show="showTabIcon">assignment_turned_in</v-icon>
          </div>
          <div
            v-if="hasRubric"
            class="sidebar-tab"
            role="button"
            aria-label="Open Rubric"
            tabindex="0"
            @click="setSidebarTab('rubric-tab')"
            @keyup.enter="setSidebarTab('rubric-tab')"
            v-show="!isBroadcast || broadcastTeacher"
            :class="{ active: sidebarTab === 'rubric-tab' }"
          >
            Rubric <v-icon v-show="showTabIcon">dns</v-icon>
          </div>
          <div
            class="sidebar-tab"
            role="button"
            aria-label="Open Notes Tab"
            tabindex="0"
            @click="setSidebarTab('notes-tab')"
            @keyup.enter="setSidebarTab('notes-tab')"
            v-show="!isBroadcast || broadcastTeacher"
            :class="{ active: sidebarTab === 'notes-tab' }"
          >
            Notes <v-icon v-show="showTabIcon">note_add</v-icon>
          </div>
          <div
            class="sidebar-tab"
            role="button"
            aria-label="Open Transcript"
            tabindex="0"
            @click="setSidebarTab('transcript-tab')"
            @keyup.enter="setSidebarTab('transcript-tab')"
            v-show="!isBroadcast || broadcastTeacher"
            :class="{ active: sidebarTab === 'transcript-tab' }"
          >
            Transcript <v-icon v-show="showTabIcon">speaker_notes</v-icon>
          </div>
          <div
            v-if="isBroadcast"
            class="sidebar-tab"
            role="button"
            :aria-label="`${termForBulb.capitalize()} Broadcast Info`"
            tabindex="0"
            @click="setSidebarTab('broadcast-info-tab')"
            @keyup.enter="setSidebarTab('broadcast-info-tab')"
            :class="{ active: sidebarTab === 'broadcast-info-tab' }"
          >
            Broadcast <v-icon v-show="showTabIcon">cast</v-icon>
          </div>
        </div>
        <div class="secondary-action-container" ref="sac">
          <bulb-info ref="bulbInfo" v-show="activeSidebarTab === 'bulb-info-tab'"></bulb-info>
          <notes ref="notes" v-show="activeSidebarTab === 'notes-tab'"></notes>
          <transcript ref="transcript" v-show="activeSidebarTab === 'transcript-tab'"></transcript>
          <rubric v-if="hasRubric" v-show="activeSidebarTab === 'rubric-tab'"></rubric>
          <broadcast-info
            v-if="isBroadcast"
            ref="broadcast-info"
            v-show="activeSidebarTab === 'broadcast-info-tab'"
          ></broadcast-info>
        </div>
      </div>
      <div class="interactions-wrapper full-height" v-show="showInteractionsTab">
        <div class="secondary-action-container">
          <div
            class="question-container"
            v-show="activeInteraction"
            :class="{ 'overflow-y-hidden': $store.state.orientation !== 'portrait' }"
          >
            <interaction-wrapper
              v-for="i in sidebarInteractions"
              :interaction-id="i.id"
              :key="'i_' + i.id"
            ></interaction-wrapper>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import BroadcastInfo from './BroadcastInfo';
import BulbInfo from './review/BulbInfo';
import InteractionWrapper from '../interaction/InteractionWrapper';
import Notes from './notes/Notes';
import Rubric from './rubric/Rubric';
import Transcript from './Transcript';
import { mapGetters, mapMutations, mapActions, mapState } from 'vuex';
/* eslint-disable */
export default {
  name: 'SidebarPanel',
  components: {
    BroadcastInfo,
    BulbInfo,
    InteractionWrapper,
    Notes,
    Rubric,
    Transcript,
  },
  props: ['sidebarStyle'],
  data() {
    return {
      expanded: false,
      tabs: null,
    };
  },
  computed: {
    ...mapState('player', [
      'isLiveSession',
      'setFirstFocus',
    ]),
    ...mapGetters('player', [
      'bulb',
      'bulbInitialized',
      'hasRubric',
      'mobile',
      'user',
      'showInteractionsTab',
      'sidebarOpen',
      'sidebarTab',
      'sidezoneOpen',
      'isBroadcast',
      'broadcastTeacher',
      'broadcastStudent',
      'termForBulb',
    ]),
    ...mapGetters('interactions', [
      'activeInteraction',
      'activeInteractionGroup',
      'activeInteractionMeta',
      'activeSidebarGroup',
      'interactions',
      'interactionMetas',
      'nextDisabled',
      'previousDisabled',
      'sidebarInteractions',
    ]),

    activeSidebarTab: {
      get() {
        return this.sidebarTab;
      },
      set(newVal) {
        this.setSidebarTab(newVal);
      },
    },
    activeSidebarGroup() {
      return _.filter(
        this.activeInteractionGroup,
        (aig) => !aig.interactionMeta.overlay && !aig.interactionMeta.hiddenByRequired
      );
    },
    showExpandButton() {
      if (this.isBroadcast && !this.bulbInitialized) return false;
      return true;
    },
    showSidebarActions() {
      return !(this.sidebarStyle === 'portrait' && this.previousDisabled && this.nextDisabled);
    },
    showTabIcon() {
      if (this.sidebarStyle === 'portrait') return false;
      if (this.mobile) return false;
      return true;
    },
  },
  methods: {
    ...mapMutations('player', [
      'recenterCaptions',
      'setInteractionRequest',
      'setShowInteractionsTab',
      'setSidebarOpen',
      'setSidebarTab',
      // 'setSidebarTransitioned'
      // 'setSnackbar',
    ]),
    ...mapActions('interactions', [
      'requestSetActiveInteraction'
    ]),
    collapse() {
      if (this.sidebarStyle !== 'portrait') {
        this.expanded = false;
        this.setSidebarOpen(false);
        document.getElementById('toggle-sidepanel').focus();
      }
    },
    expand() {
      if (this.sidebarStyle !== 'portrait') {
        this.expanded = !this.expanded;
      }
    },
    slide(direction) {
      if (!this.slideDisabled) {
        this.setSlideInteraction(direction);
      }
    },
  },
  mounted() {
    if (this.isBroadcast) this.setSidebarTab('broadcast-info-tab');
  },
  watch: {
    sidezoneOpen() {
      if (!this.sidezoneOpen) this.expanded = false;
      else if (!this.activeSidebarGroup.length) this.setShowInteractionsTab(false);
    },
    sidebarOpen() {
      if (!this.sidebarOpen) {
        this.expanded = false;
        // this.setFirstFocus(document.getElementById('toggle-sidepanel'));
      }
      // this.$nextTick(() => {
      //   if (n && !this.activeInteraction && this.user.userType === 'student') {
      //     this.setSidebarTab('bulb-info-tab');
      //   }
      //   else if (n && this.activeInteraction) {
      //     this.setSidebarTab('interactions-tab');
      //   }
      //   else if (!this.sidebarOpen) {
      //     this.expanded = false;
      //   }
      //   this.recenterCaptions()
      // })
    },
  },
};
</script>

<style>
#sidebar-panel {
  position: absolute;
  max-width: 1000px;
  background: white;
  z-index: 10;
  transition: 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  left: 0;
  right: calc(100% - 455px);
  height: 100%;
  display: flex;
  flex-direction: column;
}

#sidebar-panel.expanded {
  right: 65px;
}

.desktop #sidebar-panel {
  position: relative;
  width: 0;
}
.desktop #sidebar-panel.open {
  width: 400px;
}
.desktop #sidebar-panel.expanded {
  flex-grow: 1;
}

.landscape #sidebar-panel.open {
  left: 55px;
}

#sidebar-panel .v-tabs__div {
  min-width: 50px;
}
#sidebar-panel .review-wrapper,
#sidebar-panel .interactions-wrapper {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  margin-bottom: 55px;
  min-height: 0px;
}

#sidebar-panel .sidebar-tabs {
  display: flex;
  justify-content: center;
  background: white;
  color: #757575;
  min-height: 47px;
}
#sidebar-panel .sidebar-tab {
  width: 85px;
  text-align: center;
  display: flex;
  flex-direction: column-reverse;
  align-content: initial;
  font-size: 1em;
  margin-bottom: 2px;
  border-bottom: none;
  text-transform: uppercase;
}
#sidebar-panel .sidebar-tab.active {
  margin-bottom: 0;
  border-bottom: solid 2px var(--branding-active-hex);
}
#sidebar-panel .sidebar-tab.active,
#sidebar-panel .sidebar-tab.active i {
  color: var(--branding-active-hex) !important;
}
.portrait #sidebar-panel .sidebar-tabs {
  min-height: 34px;
}
.portrait #sidebar-panel .sidebar-tabs i {
  display: none;
}

#sidebar-panel .v-tabs__slider-wrapper {
  display: none;
}
#sidebar-panel.mobile .v-tabs__container {
  height: 35px !important;
}
#sidebar-panel.mobile .v-tabs__item i {
  display: none;
}

#sidebar-panel .v-tabs__items {
  background-color: #eceff1 !important;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}
#sidebar-panel .v-tabs__content {
  flex-grow: 1;
}
#sidebar-panel .v-tabs__item.v-tabs__item--active,
#sidebar-panel .v-tabs__item.v-tabs__item--active i {
  color: var(--branding-active-hex) !important;
}
#sidebar-actions li {
  display: inline-block;
  /*padding: .5em;*/
  cursor: pointer;
}
#sidebar-actions li.disabled {
  cursor: inherit;
}
#sidebar-actions li.disabled i {
  color: #ccc;
}

#sidebar-actions i {
  font-size: 1.25em;
}

.secondary-action-container {
  -webkit-overflow-scrolling: touch; /* allow touch scrolling  */
  overflow: auto;
  margin-top: 0.5em;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
}
.question-container {
  -webkit-overflow-scrolling: touch; /* allow touch scrolling  */
  overflow: auto;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
}
.secondary-action-container iframe {
  display: block;
}

#notes-tab,
#transcript-tab,
#bulb-info-tab {
  display: flex;
}

.sidebar-slide-enter {
  transform: translateX(-455px);
  width: 0;
}
.sidebar-slide-enter-to {
  transform: translateX(0);
  width: 400px;
}
.sidebar-slide-leave {
  transform: translateX(0);
  width: 400px;
}
.sidebar-slide-leave-to {
  transform: translateX(-455px);
  width: 0;
}
</style>
